import { Dispatch, SetStateAction, useEffect } from 'react';

import { useRouter } from 'next/navigation';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackNavigationBetweenWaters } from '@domain/analytics/trackNavigationBetweenWaters';
import { reportMykissClientError } from '@lib/errors/client';
import { EventMykissSearchResultSelected } from '@generated/events';
import { ISearchGroup } from '@app-types/search';
import { Button } from '../../Clickables/Buttons/Button';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { NonCrawlableLink } from '../../Clickables/NonCrawlableLink/NonCrawlableLink';
import { ISpeciesInfo } from '../../Search/constants';
import { Text } from '../../Text/Text';
import { trackNearbyWatersClicked } from '../helpers/trackNearbyWatersClicked';
import { trackOptionSelected } from '../helpers/trackOptionSelected';

import { EmptyResults } from './EmptyResults';

interface IGroupedOptionsProps {
  group: ISearchGroup;
  searchTerm: string;
  onCloseSearchBox: () => void;
  isFetching: boolean;
  isSearchConfirmed?: boolean;
  source: EventMykissSearchResultSelected['properties']['source'];
  onOptionSelect?: Dispatch<SetStateAction<ISpeciesInfo | undefined>>;
  hideLabel?: boolean;
}

export const GroupedOptions = ({
  group,
  searchTerm,
  onCloseSearchBox,
  isFetching,
  isSearchConfirmed = false,
  source,
  onOptionSelect,
  hideLabel = false,
}: IGroupedOptionsProps) => {
  const { currentPage } = useAnalytics();
  const router = useRouter();

  useEffect(() => {
    if (isSearchConfirmed && group.options.length > 0) {
      // Use first search result
      const option = group.options[0]!;

      trackOptionSelected(option, source, currentPage, group, searchTerm);
      onCloseSearchBox();
      try {
        router.push(option.link);
      } catch (error) {
        reportMykissClientError(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchConfirmed]);
  return (
    <div className="w-full grow py-4 pl-3">
      {!hideLabel && (
        <div className="text-midnight mb-2 text-sm font-semibold tracking-wider uppercase">
          {group.label}
        </div>
      )}

      {!isFetching && !group.options.length && (
        <Text variant="sm" className="mb-0">
          No results.
        </Text>
      )}
      {isFetching && <div data-testid="search-fetching">{EmptyResults}</div>}
      {!isFetching &&
        group.options.map(option => {
          if (option.id === 'nearby-water') {
            return (
              <NonCrawlableLink
                key={option.id}
                routerPushTo="/explore"
                onClickEvent={() => {
                  trackNearbyWatersClicked(searchTerm);
                }}
                variant="textPrimaryDarkBg"
                ariaLabel={option.title}
              >
                <Text variant="sm" className="mb-0 font-semibold">
                  {option.title}
                </Text>
              </NonCrawlableLink>
            );
          }
          if (onOptionSelect) {
            const speciesObj = { speciesId: option.id, displayName: option.title };
            return (
              <Button
                key={option.id}
                variant="textPrimary"
                className="mb-3 block"
                aria-label={option.title}
                onClick={() => {
                  onOptionSelect(speciesObj);
                  onCloseSearchBox();
                }}
              >
                {option.title}
              </Button>
            );
          }
          return (
            <InternalLink
              key={option.id}
              to={option.link}
              variant="textPrimary"
              className="mb-3 block"
              onClick={() => {
                trackOptionSelected(option, source, currentPage, group, searchTerm);
                if (group.type === 'fishing_water') {
                  trackNavigationBetweenWaters('search');
                }
                onCloseSearchBox();
              }}
            >
              <Text variant="sm" className="mb-0">
                {option.title}
              </Text>

              {option.subtitle && (
                <Text variant="xs" className="text-midnight mb-0">
                  {option.subtitle}
                </Text>
              )}
            </InternalLink>
          );
        })}
    </div>
  );
};
